import React, { forwardRef, ReactNode, useState, useContext, useCallback } from 'react';

import { useHoverMediaEventListeners } from './helpers/useHoverMediaEventListeners';
import { useResizeEventListeners } from './helpers/useResizeEventListeners';

import { getLayoutContextValue, LayoutContextValue } from './getLayoutContextValue';

export const LayoutContext = React.createContext<LayoutContextValue | null>(null);

export const LayoutContextProvider = (props: { children: ReactNode }) => {
  const [value, setValue] = useState(() => getLayoutContextValue());
  const updateValue = useCallback(() => setValue(getLayoutContextValue()), []);

  useHoverMediaEventListeners(updateValue);
  useResizeEventListeners(updateValue);

  return <LayoutContext.Provider value={value}>{props.children}</LayoutContext.Provider>;
};

const checkContextProvider = (value: LayoutContextValue | null) => {
  if (value === null) {
    // eslint-disable-next-line no-console
    /** @todo FC-739 Подумать над описанием ошибок на русском и каком-то унифицированном логгере */
    console.error(
      'Missing LayoutContextProvider in rendered components hierarchy. ' +
        'Currently layoutState will not update on resize events. ' +
        'To fix that, please add LayoutContextProvider as parent in component tree.' +
        'For example in App.jsx or Root.jsx: ' +
        'export default () => <LayoutContextProvider><AppRoutes /></LayoutContextProvider>'
    );

    return getLayoutContextValue();
  }

  return value;
};

export const useLayoutContext = () => {
  const value = useContext(LayoutContext);

  return checkContextProvider(value);
};

export const withLayoutContext = (Component) =>
  function LayoutContextWrapper(props) {
    return (
      <LayoutContext.Consumer>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        {(value) => <Component {...props} {...checkContextProvider(value)} />}
      </LayoutContext.Consumer>
    );
  };

export const withLayoutContextForwardRef = (Component) =>
  forwardRef((props, ref) => (
    <LayoutContext.Consumer>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      {(value) => <Component {...props} ref={ref} {...checkContextProvider(value)} />}
    </LayoutContext.Consumer>
  ));
